import React, { useState } from 'react';
import { CountLabel, GradientText, GradientTextAction, Poppins, Spacer } from 'src/common';
import { TabWrap } from 'src/components/TabsMenu';
import styled from 'styled-components';
import GenericTable from 'src/components/GenericTable';
import { useMembersColumns } from './useMembersColumns';
import { useInvitesColumns } from './useInvitesColumns';
import { TableIds } from 'src/api/types/misc';
import { OrganisationQuery } from 'src/graphql/_generated';
import { useRequestsColumns } from './useRequestsColumns';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { AddUserModal, UserModal } from 'src/components/modals';
import { useCache } from 'src/graphql/useCache';

const Div = styled(TabWrap)`
  background-color: ${({ theme }) => theme.colors.white};

  .head {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .label {
    color: ${({ theme }) => theme.colors.cflowerBlue};
    font-size: 22px;
    &__total {
      color: ${({ theme }) => theme.colors.prussianBlue};
      font-size: 24px;
      font-weight: 600;
    }
  }

  .header-label {
    font-weight: bold;
    font-size: 18px;
    color: ${({ theme }) => theme.colors.cflowerBlue};
  }

  .separator {
    height: 1px;
    width: 100%;
    background-color: ${({ theme }) => theme.colors.stroke};
  }
`;

interface MembersProps {
  organisation: NonNullable<OrganisationQuery['organisation']>;
}

export const Members: React.FC<MembersProps> = ({ organisation }) => {
  const [addUserModalOpen, setAddUserModalOpen] = useState<{ name: string; email: string } | null>(null);
  const [profileModalId, setProfileModalId] = useState<number | null>(null);
  const { refetchQuery } = useCache();

  const { columns, GTColumnSelectAnchorExported } = useMembersColumns({
    data: organisation.users,
    orgId: organisation.id,
    setProfileModalId,
  });

  const { columns: columnsRequest, GTColumnSelectAnchorExported: GTColumnSelectAnchorExportedRequest } =
    useRequestsColumns({ organisation });

  const { columns: columnsInvite, GTColumnSelectAnchorExported: GTColumnSelectAnchorExportedInvite } =
    useInvitesColumns({ organisation });

  return (
    <>
      {profileModalId && (
        <UserModal
          onClose={() => setProfileModalId(null)}
          externalUserId={profileModalId}
          externalUserEmail={organisation.users.find((u) => +u.id === profileModalId)?.email || undefined}
          orgId={organisation.id}
          onUserUpdated={() => refetchQuery({ include: ['organisation'] })}
          highlightRoles
        />
      )}
      {addUserModalOpen && (
        <AddUserModal
          id={organisation.id}
          type="organisation"
          onClose={() => setAddUserModalOpen(null)}
          emailsInUse={[...organisation.invites, ...organisation.requests, ...organisation.users].map(
            (el) => el.email || '',
          )}
          initialValues={addUserModalOpen}
        />
      )}
      <Div>
        {/* MEMBERS */}
        <GradientText>MEMBERS</GradientText>
        <Spacer $px={30} />
        <div className="head">
          <CountLabel label="member" length={organisation.users.length} />
          <GradientTextAction
            onClick={() => {
              setAddUserModalOpen({ name: '', email: '' });
              mpEvent(MPEvents.ButtonClick, {
                button: 'Add a user',
                tags: ['ORGANISATION'],
              });
            }}
            weight={500}
          >
            + Add a User
          </GradientTextAction>
        </div>
        <Spacer $px={20} />
        <GenericTable
          tableId={TableIds.orgMembers}
          data={organisation.users}
          columns={columns}
          GTColumnSelectAnchorExported={GTColumnSelectAnchorExported}
          searchable={['name', 'email', 'role']}
        />
        {/* REQUESTS */}
        {organisation.requests.length > 0 && organisation.actionsOnThisOrganisation?.canSeeRequests && (
          <>
            <Spacer $px={30} />
            <Poppins className="header-label">REQUESTS</Poppins>
            <Spacer $px={16} />
            <CountLabel label="request" length={organisation.requests.length} />
            <Spacer $px={5} />
            <GenericTable
              tableId={TableIds.orgRequests}
              data={organisation.requests}
              columns={columnsRequest}
              GTColumnSelectAnchorExported={GTColumnSelectAnchorExportedRequest}
              searchable={['email', 'name']}
            />
          </>
        )}
        {/* INVITES */}
        {organisation.invites.length > 0 && organisation.actionsOnThisOrganisation?.canSeeInvites && (
          <>
            <Spacer $px={30} />
            <Poppins className="header-label">INVITES</Poppins>
            <Spacer $px={16} />
            <CountLabel label="invite" length={organisation.invites.length} />
            <Spacer $px={5} />
            <GenericTable
              tableId={TableIds.orgInvites}
              data={organisation.invites}
              columns={columnsInvite}
              GTColumnSelectAnchorExported={GTColumnSelectAnchorExportedInvite}
              searchable={['email', 'name']}
            />
          </>
        )}
      </Div>
    </>
  );
};
